import React from "react";
import PropTypes from "prop-types";
import PortalDropdown, { DropdownMenuItem } from "views_shared/app/javascript/components/PortalDropdown";
import ListRow from "../ListRow";

/**
 * RepresentRow — one entity in a <RepresentsSection> tab: the shared <ListRow> (linked avatar, linked
 * title, guarded "Title at Company" subtitle, chips) plus the row menu carrying the removal.
 * Model-agnostic — it renders the plain display `row` the engine produced and knows nothing about
 * representations.
 *
 * The menu's toggle is named for what it does — open a menu — not for the item inside it: the control
 * itself removes nothing, and "Remove <name>" would also read as the confirmation's own Remove button
 * to anyone querying by accessible name. The item opens the section's confirmation; the whole row goes
 * back so the engine can later resolve it to the representation id(s) to destroy.
 *
 * The surrounding <li> owns the divider between rows.
 *
 * @param {Object} props
 * @param {Object} props.row - `{ id, href, imageUrl?, title, subtitleTitle?, subtitleCompanyName?,
 *   subtitleCompanyHref?, tags? }`
 * @param {Function} [props.onRemove] - `(row) => void`; omitted/null hides the row menu entirely
 */
function RepresentRow({ row, onRemove = null }) {
  const actions = onRemove ? (
    <PortalDropdown
      toggleAriaLabel={`More actions for ${row.title}`}
      toggleIcon="fa-solid fa-ellipsis-vertical"
      toggleClassName="btn btn-secondary btn-icon rounded-2 text-primary-60 border-0 shadow-none"
    >
      <DropdownMenuItem icon="fa-solid fa-trash" label="Stop representing" onClick={() => onRemove(row)} />
    </PortalDropdown>
  ) : null;

  return (
    <ListRow
      href={row.href}
      imageUrl={row.imageUrl}
      title={row.title}
      subtitleTitle={row.subtitleTitle}
      subtitleCompanyName={row.subtitleCompanyName}
      subtitleCompanyHref={row.subtitleCompanyHref}
      tags={row.tags}
      actions={actions}
    />
  );
}

RepresentRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    href: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitleTitle: PropTypes.string,
    subtitleCompanyName: PropTypes.string,
    subtitleCompanyHref: PropTypes.string,
    tags: PropTypes.node,
  }).isRequired,
  onRemove: PropTypes.func,
};

export default RepresentRow;
